import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import Heading, {
  HeadingSize,
  HeadingTag,
} from '@/components/ui/Heading/Heading';
import usePreview from './usePreview';
import { useSearchParams } from 'next/navigation';

// Implementation based on the following feature:
// https://nextjs.org/docs/pages/building-your-application/configuring/preview-mode

interface SwitchProps {
  isPreview: boolean;
  isLoading: boolean;
  handlePreview: () => void;
}

const Switch = ({ isPreview, isLoading, handlePreview }: SwitchProps) => {
  // Uses local state to toggle the button until the page is reloaded
  const [enabled, setEnabled] = useState(isPreview);

  useEffect(() => {
    setEnabled(isPreview);
  }, [isPreview]);

  return (
    <button
      onClick={() => {
        setEnabled((enabled) => !enabled);
        handlePreview();
      }}
      className={classNames(
        'text-xs font-bold relative flex items-center w-16 h-8 rounded-full transition-all duration-300',
        enabled ? 'bg-blue-deep' : 'border-2 border-blue-deep bg-white',
        isLoading && 'opacity-50 cursor-not-allowed',
      )}
      disabled={isLoading}
    >
      <span
        className={classNames(
          'absolute inset-y-0 left-1 flex items-center transition-transform duration-300',
          enabled ? 'translate-x-2 text-white' : 'translate-x-7 text-blue-deep',
        )}
      >
        {enabled ? 'ON' : 'OFF'}
      </span>
      <span
        className={classNames(
          'absolute right-1  rounded-full transition-transform duration-300',
          enabled
            ? 'w-6 h-6 translate-x-0 bg-white'
            : 'w-4 h-4 -translate-x-8 border-2 bg-blue-deep border-blue-deep',
        )}
      ></span>
    </button>
  );
};

const ContentfulPreviewControls = () => {
  const { environment, isPreview, handlePreview, handleRefresh, isLoading } =
    usePreview();
  const searchParams = useSearchParams();
  const env = searchParams?.get('env');
  return (
    <div className="flex items-center justify-between mb-6 gap-10">
      <div className="flex flex-col lg:max-w-32">
        <Heading
          tag={HeadingTag.H4}
          size={HeadingSize.H4}
          text="Preview Mode"
          hoverEffect
        />
        {isPreview && (
          <div className="flex items-center gap-2">
            <small className="leading-4">{environment}</small>
            {/* Refresh environment while keeping preview mode enabled */}
            {env && env !== environment && (
              <button
                onClick={handleRefresh}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors duration-200 disabled:opacity-50"
                disabled={isLoading}
                title="Refresh preview"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-gray-600"
                >
                  <path d="M1 4v6h6"></path>
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center relative">
        {isLoading && (
          <div className="animate-spin h-4 w-4 border-2 border-charcoal rounded-full border-t-transparent absolute -left-6" />
        )}
        <Switch
          isPreview={isPreview}
          isLoading={isLoading}
          handlePreview={handlePreview}
        />
      </div>
    </div>
  );
};

export default React.memo(ContentfulPreviewControls);
